import type { ReactElement, SVGProps, MouseEvent } from 'react';
import { isValidElement, cloneElement } from 'react';

import { cn } from '@simplywallst/ui-core/next';

import CheckSolid from '@simplywallst/ui-core/icons/CheckSolid';

type Props = {
  label: string;
  value: string;
  selected?: boolean;
  updateCount?: number;
  onClick: (e: MouseEvent<HTMLButtonElement>) => void;
  icon: ReactElement<SVGProps<SVGSVGElement>>;
};

export const Tab = ({
  label,
  value,
  onClick,
  selected = false,
  updateCount,
  icon,
}: Props) => {
  const childWithProps = isValidElement(icon)
    ? cloneElement(icon, {
        width: '24',
        height: '24',
        x: '8',
        y: '8',
        className: cn(
          'group-hover:fill-good',
          selected ? 'fill-good' : 'fill-softer'
        ),
      })
    : icon;

  return (
    <button
      aria-label={label}
      aria-selected={selected}
      type="button"
      role="tab"
      value={value}
      onClick={onClick}
      className={cn(
        'group inline-grid items-center gap-x1 rounded-md bg-transparent p-x1 @[768px]:w-full @[768px]:grid-cols-[40px_1fr] @[768px]:hover:bg-surface-1',
        selected &&
          'bg-surface-2 @[768px]:grid-cols-[40px_1fr_24px] @[768px]:bg-surface-1'
      )}
    >
      <svg
        width="40"
        height="40"
        viewBox="0 0 40 40"
        className="hidden @[768px]:block"
      >
        <circle
          cx="20"
          cy="20"
          r="20"
          className={cn(
            'group-hover:fill-good-subtle',
            selected ? 'fill-good-subtle' : 'fill-legacy-phantom'
          )}
        />
        {childWithProps}
      </svg>
      <div
        className={cn(
          'grid grid-cols-[1fr_max-content] items-center text-left @[768px]:grid-cols-1 @[768px]:grid-rows-[auto_auto]',
          updateCount && 'gap-x-x0_75'
        )}
      >
        <span
          className={cn(
            'text-xs font-strong',
            selected ? 'text-solid' : 'text-soft'
          )}
        >
          {label}
        </span>
        <div
          className={cn(
            'text-softer group-hover:text-soft',
            selected && 'text-soft'
          )}
        >
          {updateCount ? (
            <span className="grid grid-cols-[6px_1fr] items-center gap-x0_5 text-tiny">
              <svg width="6" height="6" viewBox="0 0 6 6">
                <circle cx="3" cy="3" r="3" className="fill-attention" />
              </svg>
              <span className="hidden @[768px]:block">
                {updateCount} new updates
              </span>
            </span>
          ) : null}
        </div>
      </div>
      {selected && <CheckSolid className="hidden fill-solid @[768px]:block" />}
    </button>
  );
};
